/**
 * Assembly Form Dialog Component
 * Main orchestrator component that combines all form sections
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  TextField
} from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import AssemblyLifecycleSection from './AssemblyLifecycleSection';
import { useAssemblyForm } from './useAssemblyForm';
import AssemblyFormFields from './AssemblyFormFields';
import { generateAssemblyCode } from '../components/componentFormUtils';
import AssemblyTypeConfigurationSection from './AssemblyTypeConfigurationSection';
import CustomAssemblyTypeConfigurationSection from './CustomAssemblyTypeConfigurationSection';
import TimeSinceRecordsTable from './TimeSinceRecordsTable';
import AirframeComparisonSection from '../components/ComponentFormFields/AirframeComparisonSection';
import AssemblySelfComparisonSection from './AssemblySelfComparisonSection';
import AssemblyLiveComparisonSection from './AssemblyLiveComparisonSection';

const AssemblyFormDialog = ({
  open,
  onClose,
  selectedAssembly,
  selectedAircraft,
  formData,
  setFormData,
  dropdownOptions,
  onSubmit,
  onRefreshDropdowns,
  readOnly = false,
  onSwitchToEdit,
  assemblies = [],
  currentUsageTotals = null,
  effectiveTlbUsageMapping = null,
  onExportAircraftCsv,
  exporting = false,
  canExportAircraftCsv = false
}) => {
  // Use custom hook for all form logic, state, and handlers
  const {
    // State
    assemblyTypeConfig,
    configLoading,
    timeSinceRecords,
    filteredModelVariants,
    
    // Handlers
    handleManufacturerChange,
    handleAssemblyTypeChange,
    handleConfigValueChange,
    getConfigValue,
    handleAddTimeSinceRecord,
    handleUpdateTimeSinceRecord,
    handleRemoveTimeSinceRecord,
    handleNavigateToAddAssemblyType,
    handleNavigateToAddPart,
    handlePartMasterSelect,
    handleAddCustomColumn,
    handleRemoveCustomColumn,
    handleCustomColumnChange,
    handleCustomColumnApplyTemplate,
    getCustomConfigValue,
    handleCustomConfigValueChange,
    
    // Utility functions
    validateYearsInput,
    validateCalendarDaysInput,
    calculateEndDate,
    prepareTimeSinceRecordsForSubmission
  } = useAssemblyForm({
    open,
    selectedAssembly,
    selectedAircraft,
    formData,
    setFormData,
    dropdownOptions,
    onRefreshDropdowns
  });

  const [lifecycleDialogOpen, setLifecycleDialogOpen] = useState(false);

  // Build label options for Time Since Records Label column:
  // - Master-defined columns (Assembly Type Configuration)
  // - Custom columns (Custom Assembly Type Configuration)
  const masterColumnLabels = (assemblyTypeConfig?.columns || [])
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(col => (col.label || '').trim())
    .filter(Boolean);

  const customColumnLabels = (Array.isArray(formData.customColumns) ? formData.customColumns : [])
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(col => (col.label || '').trim())
    .filter(Boolean);

  const labelOptions = Array.from(
    new Set([...masterColumnLabels, ...customColumnLabels])
  );

  const handleSubmit = () => {
    const cleanedTimeSinceRecords = prepareTimeSinceRecordsForSubmission(timeSinceRecords);
    const updatedFormData = { ...formData, timeSinceRecords: cleanedTimeSinceRecords };
    onSubmit(updatedFormData);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {selectedAssembly ? 'Edit Assembly' : 'Add Assembly'}
      </DialogTitle>
      <DialogContent>
        <Box sx={readOnly ? { opacity: 0.9 } : undefined}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Assembly Code"
                value={selectedAssembly ? (generateAssemblyCode(selectedAssembly) || '—') : '—'}
                InputProps={{
                  readOnly: true,
                  sx: { bgcolor: 'action.hover', fontFamily: 'monospace' }
                }}
                size="small"
                helperText="Generated; cannot be changed."
              />
            </Grid>
            <AssemblyFormFields
              formData={formData}
              setFormData={setFormData}
              dropdownOptions={dropdownOptions}
              filteredModelVariants={filteredModelVariants}
              handleAssemblyTypeChange={handleAssemblyTypeChange}
              handleManufacturerChange={handleManufacturerChange}
              handleNavigateToAddAssemblyType={handleNavigateToAddAssemblyType}
              handleNavigateToAddPart={handleNavigateToAddPart}
              handlePartMasterSelect={handlePartMasterSelect}
              readOnly={readOnly}
            />
            
            <AssemblyTypeConfigurationSection
              formData={formData}
              assemblyTypeConfig={assemblyTypeConfig}
              configLoading={configLoading}
              getConfigValue={getConfigValue}
              handleConfigValueChange={handleConfigValueChange}
              readOnly={readOnly}
            />

            <CustomAssemblyTypeConfigurationSection
              formData={formData}
              assemblyTypeConfig={assemblyTypeConfig}
              configLoading={configLoading}
              handleAddCustomColumn={handleAddCustomColumn}
              handleRemoveCustomColumn={handleRemoveCustomColumn}
              handleCustomColumnChange={handleCustomColumnChange}
              handleCustomColumnApplyTemplate={handleCustomColumnApplyTemplate}
              getCustomConfigValue={getCustomConfigValue}
              handleCustomConfigValueChange={handleCustomConfigValueChange}
              readOnly={readOnly}
            />
            
            <TimeSinceRecordsTable
              timeSinceRecords={timeSinceRecords}
              handleAddTimeSinceRecord={handleAddTimeSinceRecord}
              handleUpdateTimeSinceRecord={handleUpdateTimeSinceRecord}
              handleRemoveTimeSinceRecord={handleRemoveTimeSinceRecord}
              validateYearsInput={validateYearsInput}
              validateCalendarDaysInput={validateCalendarDaysInput}
              isAirframeAssembly={selectedAssembly?.typeName === 'Airframe' || formData.typeName === 'Airframe'}
              readOnly={readOnly}
              labelOptions={labelOptions}
            />
            {selectedAssembly && (
              <AssemblyLiveComparisonSection
                timeSinceRecords={timeSinceRecords}
                assemblies={assemblies}
                selectedAssembly={selectedAssembly}
                currentUsageTotals={currentUsageTotals}
                effectiveTlbUsageMapping={effectiveTlbUsageMapping}
              />
            )}
            <AirframeComparisonSection
              timeSinceRecords={timeSinceRecords}
              assemblies={assemblies}
              entityLabel="Assembly"
            />
            <AssemblySelfComparisonSection timeSinceRecords={timeSinceRecords} />
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        {selectedAssembly && canExportAircraftCsv && (
          <Button
            onClick={onExportAircraftCsv}
            startIcon={<FileDownload />}
            disabled={exporting}
          >
            {exporting ? 'Downloading CSV...' : 'Download Aircraft CSV'}
          </Button>
        )}
        {selectedAssembly && (
          <Button onClick={() => setLifecycleDialogOpen(true)}>
            View Lifecycle
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        {readOnly ? (
          <Button
            onClick={onSwitchToEdit}
            variant="contained"
            disabled={!onSwitchToEdit}
          >
            Edit Assembly
          </Button>
        ) : (
          <Button onClick={handleSubmit} variant="contained">
            {selectedAssembly ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>

      <Dialog open={lifecycleDialogOpen} onClose={() => setLifecycleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Assembly Lifecycle History</DialogTitle>
        <DialogContent>
          <AssemblyLifecycleSection assemblyId={selectedAssembly?._id} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLifecycleDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AssemblyFormDialog;
